import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import GroupDocuments from './GroupDocuments';
import FileRowList from '../Shared/FileRow/List';
import ResourceDetailTitle from '../Shared/ResourceDetail/ResourceDetailTitle';
import ResourceDetailTabs from '../Shared/ResourceDetail/ResourceDetailTabs';
import RegulationContext from '../../contexts/RegulationContext';
import AppDivisionContext from '../../contexts/AppDivisionContext';
import TenantFeatureContext from '../../contexts/TenantContext';
import RolesContext from '../../contexts/RolesContext';
import { LoadingMessage, Modal, TooltipContextProvider } from '@stormwater-software/lib-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faArrowLeft,
  faPlus,
  faTrashAlt,
  faEdit,
  faCloudUploadAlt,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import uploadApi from '../../utils/api/upload';
import WebViewerModal from '../Shared/PDFEditor/WebViewerModal';
import FileRenameModal from '../Shared/FileRow/FileRenameModal';
import { toast } from 'react-toastify';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import {
  useDeleteRegulationDoc,
  useGroupDocs,
  useBmpDocs,
  useUpdateRegulationDoc,
  useUploadRegulationDoc,
  useUploadBmpDoc,
  useOnMoveRegulationDoc,
  useOnDropRegulationDoc,
  useDeleteBmpDoc,
  ScopeType,
  useUpdateBmpDoc,
  useOnMoveBmpDoc,
  useOnDropBmpDoc,
} from '../../hooks/groupDocs';

import useResource from '../../hooks/resource';
import ResourceSearch from '../Shared/Resource/Search';
import { ActionButtons } from '../Shared/ActionButtons';
import GroupDetailsForm from '../Groups/Forms/GroupDetailsForm';
import groupApi from '../../utils/api/group';
import ResourceDetailDelete from '../Shared/ResourceDetail/ResourceDetailDelete';
import AppContext from '../../contexts/AppContext';

const GroupDetailUI = (props) => {
  const regulationContext = useContext(RegulationContext);
  const { getPath, appDivisionId } = useContext(AppDivisionContext);
  const featureContext = useContext(TenantFeatureContext);
  const { id } = props.match.params;
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [panel, setPanel] = useState(['Regulations', 'Legend Details']);
  const groupDocsQuery = useGroupDocs(id);
  const bmpDocsQuery = useBmpDocs(id, appDivisionId);
  const deleteRegulationDocMutation = useDeleteRegulationDoc(id);
  const deleteBmpDocMutation = useDeleteBmpDoc(id);
  const uploadRegulationMutation = useUploadRegulationDoc(id);
  const uploadBmpMutation = useUploadBmpDoc(id);
  const updateRegulationMutation = useUpdateRegulationDoc(id);
  const updateBmpMutation = useUpdateBmpDoc(id);
  const moveRegulationMutation = useOnMoveRegulationDoc(id);
  const moveBmpMutation = useOnMoveBmpDoc(id);
  const dropRegulationMutation = useOnDropRegulationDoc();
  const dropBmpMutation = useOnDropBmpDoc();
  const { search } = useResource({ resource: 'divisionProjects' });
  const { query, setQuery } = search;
  const { permCheck } = props;
  const [disableDelete, setDisableDelete] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  // Subfolder management state for master Regulation Group
  const [showAddSubFolderModal, setShowAddSubFolderModal] = useState(false);
  const [newSubFolderName, setNewSubFolderName] = useState('');
  const [selectedSubFolderId, setSelectedSubFolderId] = useState(null);
  const [subFolderError, setSubFolderError] = useState('');
  const [subFolderLoading, setSubFolderLoading] = useState(false);
  const [showOrganizeModal, setShowOrganizeModal] = useState(false);
  const [tempOrderedFolders, setTempOrderedFolders] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [deleteSubFolderError, setDeleteSubFolderError] = useState('');
  const [organizeError, setOrganizeError] = useState('');

  // Viewer state for the Regulations list. The list renders FileRowList
  // directly (it no longer goes through GroupDocuments), so this page has to
  // own the WebViewer modals that back the row's View and Download actions.
  const [showEditor, setShowEditor] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [viewerDoc, setViewerDoc] = useState({ guid: '', id: null, name: '' });
  const [showRename, setShowRename] = useState(false);
  const [renameDoc, setRenameDoc] = useState({ guid: '', name: '' });
  const editPermissionList = [
    'Client Inspector',
    'Inspector',
    'Project Engineer',
    'Regional Manager (External)',
    'Supervising Engineer',
  ];

  const formatScope = (scope) => {
    return `${scope}_docs`;
  };

  const onDocumentDelete = (docId, scope, groupId) => {
    formatScope(scope) === ScopeType.REGULATIONS_DOCS || groupId === 0
      ? deleteRegulationDocMutation.mutate({
          scope: formatScope(scope),
          docId,
          groupId,
        })
      : deleteBmpDocMutation.mutate({ scope: formatScope(scope), docId });
    bmpDocsQuery.refetch();
  };

  const updateGroup = (group) => {
    return regulationContext.updateRegulation(group);
  };

  const deleteGroup = () => {
    props.history.push(getPath('/groups'));
  };

  const onDocumentUpload = (file, scope, legendItemId) => {
    formatScope(scope) === ScopeType.REGULATIONS_DOCS ||
    (formatScope(scope) === ScopeType.BMP_DOCS && !legendItemId)
      ? uploadRegulationMutation.mutate({
          scope: formatScope(scope),
          file,
          fixed: true,
        })
      : uploadBmpMutation.mutate({
          scope: formatScope(scope),
          file,
          legendItemId,
        });
    bmpDocsQuery.refetch();
  };

  const onDocumentEdit = (file, scope, fixed) => {
    formatScope(scope) === ScopeType.REGULATIONS_DOCS || !fixed
      ? updateRegulationMutation.mutate({
          scope: formatScope(scope),
          file,
          fixed: true,
        })
      : updateBmpMutation.mutate({ scope: formatScope(scope), file });
    bmpDocsQuery.refetch();
  };

  const onMove = ({ scope, hoverIndex, dragIndex, legendItemId }) => {
    formatScope(scope) === ScopeType.REGULATIONS_DOCS
      ? moveRegulationMutation.mutate({
          scope: formatScope(scope),
          hoverIndex,
          dragIndex,
          legendItemId,
        })
      : moveBmpMutation.mutate({
          scope: formatScope(scope),
          hoverIndex,
          dragIndex,
          legendItemId,
        });
  };

  const onDrop = ({ scope, hoverIndex, dragIndex, document, index }) => {
    formatScope(scope) === ScopeType.REGULATIONS_DOCS
      ? dropRegulationMutation.mutate({
          scope: formatScope(scope),
          hoverIndex,
          dragIndex,
          document,
          index,
        })
      : dropBmpMutation.mutate({
          scope: formatScope(scope),
          hoverIndex,
          dragIndex,
          document,
          index,
        });
  };

  // Prefer the API's own message. A generic "failed" line — or worse, a silent
  // catch — is what hid the folder-delete crash from QA for a whole round.
  const apiErrorMessage = (err, fallback) =>
    (err && err.response && err.response.data && err.response.data.message) ||
    fallback;

  const handleAddSubFolder = async () => {
    if (!newSubFolderName.trim()) {
      setSubFolderError('Folder name cannot be empty');

      return;
    }
    setSubFolderLoading(true);
    setSubFolderError('');
    try {
      await uploadApi.groupSubFolder.add(id, newSubFolderName.trim());
      setNewSubFolderName('');
      setShowAddSubFolderModal(false);
      groupDocsQuery.refetch();
    } catch (e) {
      setSubFolderError(apiErrorMessage(e, 'Failed to create folder'));
    } finally {
      setSubFolderLoading(false);
    }
  };

  // Associate an already-uploaded file with the master Regulation Group
  // (optionally a subfolder) and refresh the list, showing an "uploading"
  // indicator until the refreshed data comes back.
  const uploadToRegulations = async (uploadedFile, subFolderId = null) => {
    setUploading(true);
    setUploadError('');
    try {
      await uploadApi.group.create(
        id,
        uploadedFile.id,
        'Regulations',
        subFolderId,
      );
      await groupDocsQuery.refetch();
    } finally {
      setUploading(false);
    }
  };

  // Same list the project Documents page allows, including the non-standard
  // image/jpg some browsers report, so a file accepted there is accepted here.
  const REGULATION_UPLOAD_TYPES = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'application/pdf',
  ];

  // A file input's `accept` list is only a picker hint — it can be bypassed by
  // switching the picker to "All files" — so the type is checked here too, and
  // the reason is shown instead of the file being silently ignored.
  const rejectUnsupportedFile = (file) => {
    if (REGULATION_UPLOAD_TYPES.includes(file.type)) {
      return false;
    }

    setUploadError(
      `"${file.name}" was not uploaded. Regulation documents must be a PDF, PNG or JPEG file.`,
    );

    return true;
  };

  // The row's View / Download actions are only wired up for PDFs; FileRow
  // sends every other type straight to its download URL.
  const openRegulationDoc = (doc, mode) => {
    setViewerDoc({ guid: doc.GUID, id: doc.id, name: doc.name });
    if (mode === 'download') {
      toast('Download has started');
      setShowDownload(true);
    } else {
      setShowEditor(true);
    }
  };

  // Renaming a regulation document. GroupDocuments used to own this; the
  // rewritten tab dropped it, which is why Rename disappeared from the row menu.
  const openRenameDoc = (doc) => {
    setRenameDoc({ guid: doc.GUID, name: doc.name });
    setShowRename(true);
  };

  const handleRenameDoc = async (newName) => {
    // The endpoint's third argument only picks the doc-type label echoed back in
    // the response; omitting it yields 'Regulations', which is what we want.
    const renamed = await uploadApi.rename(renameDoc.guid, newName);

    // uploadApi.rename swallows its own errors and resolves undefined, so an
    // empty result is the only failure signal available here. Keep the modal
    // open rather than closing it over an unchanged name.
    if (!renamed) {
      toast.error('The document could not be renamed. Please try again.');

      return;
    }

    setShowRename(false);
    await groupDocsQuery.refetch();
  };

  const closeRegulationViewer = () => {
    setShowEditor(false);
    setShowDownload(false);
  };

  // Roles in editPermissionList are read-only for documents; the viewer also
  // needs update rights on Regulations before it will allow saving edits.
  const canEditRegulationDocs =
    permCheck('update', 'Regulations') &&
    !editPermissionList.includes(props.role);

  // Build unified mixed list from group_docs_jt + regulation_sub_folders.
  // Uses regulation_sub_folder_id FK (not order ranges) to determine folder membership.
  const unifiedDocs = useMemo(() => {
    const data = groupDocsQuery.data;

    if (!data) return [];

    const masterFolders = (data.regulation_sub_folders || []).sort(
      (a, b) => a.order - b.order,
    );

    const jt = data.group_docs_jt || [];
    const allDocs = jt.map((j) => {
      if (j.upload) {
        return {
          ...j.upload,
          GroupUploadModel: {
            id: j.id,
            order: j.order ?? 0,
            upload_id: j.upload_id,
            document_group_id: j.document_group_id,
            regulation_sub_folder_id: j.regulation_sub_folder_id ?? null,
          },
          doctypes: j.doc_type ? [{ name: j.doc_type.name }] : [],
        };
      }
      // cache-mutated flat upload shape
      return {
        ...j,
        GroupUploadModel: {
          id: j.id,
          order: j.order ?? 0,
          upload_id: j.id,
          document_group_id: j.document_group_id,
          regulation_sub_folder_id: j.regulation_sub_folder_id ?? null,
        },
        doctypes: j.doctypes || [],
      };
    });

    const foldersWithDocs = masterFolders.map((folder) => {
      const nested = allDocs
        .filter(
          (doc) => doc.GroupUploadModel.regulation_sub_folder_id === folder.id,
        )
        .sort((a, b) => a.GroupUploadModel.order - b.GroupUploadModel.order);

      return {
        ...folder,
        sub_doc_type: { name: folder.name, id: folder.id },
        group_docs: nested,
        is_regulation_folder: true,
      };
    });

    const topLevel = allDocs
      .filter((doc) => doc.GroupUploadModel.regulation_sub_folder_id === null)
      .sort((a, b) => a.GroupUploadModel.order - b.GroupUploadModel.order)
      .map((doc) => ({
        ...doc,
        ProjectUploadModel: { project_sub_doc_type_id: null },
      }));

    return [...foldersWithDocs, ...topLevel].sort((a, b) => {
      const oa = a.sub_doc_type ? a.order : a.GroupUploadModel.order;
      const ob = b.sub_doc_type ? b.order : b.GroupUploadModel.order;

      return oa - ob;
    });
  }, [groupDocsQuery.data]);

  // Derive the open subfolder from the freshly-fetched unified list (keyed by
  // id) instead of holding a snapshot. This keeps the subfolder view in sync
  // after uploads/renames/refetches without requiring a manual page refresh.
  const selectedSubFolder = useMemo(() => {
    if (selectedSubFolderId == null) return null;

    return (
      unifiedDocs.find(
        (item) => item.is_regulation_folder && item.id === selectedSubFolderId,
      ) || null
    );
  }, [unifiedDocs, selectedSubFolderId]);

  // Apply the search query to the top-level unified list. A folder is kept if
  // its own name matches; otherwise it is kept only with its matching docs.
  const filteredDocs = useMemo(() => {
    const q = (query || '').trim().toLowerCase();

    if (!q) return unifiedDocs;
    const matches = (name) => (name || '').toLowerCase().includes(q);

    return unifiedDocs.reduce((acc, item) => {
      if (item.is_regulation_folder) {
        if (matches(item.name)) {
          acc.push(item);
        } else {
          const nested = (item.group_docs || []).filter((doc) =>
            matches(doc.name),
          );

          if (nested.length) acc.push({ ...item, group_docs: nested });
        }
      } else if (matches(item.name)) {
        acc.push(item);
      }

      return acc;
    }, []);
  }, [unifiedDocs, query]);

  // Apply the search query within the open subfolder, using the same rule as
  // the list above: a folder whose own name matches keeps all of its documents.
  // Filtering the contents of a folder that matched by name would open it empty.
  const filteredSubFolderDocs = useMemo(() => {
    const docsList = (selectedSubFolder && selectedSubFolder.group_docs) || [];
    const q = (query || '').trim().toLowerCase();

    if (!q) return docsList;

    const folderNameMatches = (
      (selectedSubFolder && selectedSubFolder.name) ||
      ''
    )
      .toLowerCase()
      .includes(q);

    if (folderNameMatches) return docsList;

    return docsList.filter((doc) =>
      (doc.name || '').toLowerCase().includes(q),
    );
  }, [selectedSubFolder, query]);

  const handleOpenOrganize = () => {
    setOrganizeError('');
    setTempOrderedFolders([...unifiedDocs]);
    setShowOrganizeModal(true);
  };

  const handleOrganizeDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const reordered = Array.from(tempOrderedFolders);
    const [removed] = reordered.splice(result.source.index, 1);

    reordered.splice(result.destination.index, 0, removed);
    setTempOrderedFolders(reordered);
  };

  const handleSaveOrganize = async () => {
    try {
      await uploadApi.group.sortDocs(id, tempOrderedFolders, null);
      setShowOrganizeModal(false);
      groupDocsQuery.refetch();
    } catch (e) {
      setOrganizeError(apiErrorMessage(e, 'Could not save the new order.'));
    }
  };

  const [showSubFolderOrganizeModal, setShowSubFolderOrganizeModal] =
    useState(false);
  const [tempSubFolderDocs, setTempSubFolderDocs] = useState([]);
  const [showDeleteSubFolderModal, setShowDeleteSubFolderModal] =
    useState(false);
  const [deleteSubFolderLoading, setDeleteSubFolderLoading] = useState(false);
  const [showRenameSubFolderModal, setShowRenameSubFolderModal] =
    useState(false);
  const [renameSubFolderName, setRenameSubFolderName] = useState('');
  const [renameSubFolderError, setRenameSubFolderError] = useState('');
  const [renameSubFolderLoading, setRenameSubFolderLoading] = useState(false);

  const handleOpenSubFolderOrganize = () => {
    setOrganizeError('');
    setTempSubFolderDocs([
      ...(selectedSubFolder ? selectedSubFolder.group_docs || [] : []),
    ]);
    setShowSubFolderOrganizeModal(true);
  };

  const handleSubFolderOrganizeDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const reordered = Array.from(tempSubFolderDocs);
    const [removed] = reordered.splice(result.source.index, 1);

    reordered.splice(result.destination.index, 0, removed);
    setTempSubFolderDocs(reordered);
  };

  const handleSaveSubFolderOrganize = async () => {
    try {
      await uploadApi.group.sortDocs(id, tempSubFolderDocs, selectedSubFolder);
      setShowSubFolderOrganizeModal(false);
      groupDocsQuery.refetch();
    } catch (e) {
      setOrganizeError(apiErrorMessage(e, 'Could not save the new order.'));
    }
  };

  const handleDeleteSubFolder = async () => {
    setDeleteSubFolderLoading(true);
    try {
      await uploadApi.groupSubFolder.delete(
        regulationContext.regulation.id,
        selectedSubFolder.id,
        // This page only ever manages the group's own folders.
        'master',
      );
      setShowDeleteSubFolderModal(false);
      setSelectedSubFolderId(null);
      groupDocsQuery.refetch();
    } catch (e) {
      setDeleteSubFolderError(
        apiErrorMessage(e, 'Could not delete this Sub Folder.'),
      );
    } finally {
      setDeleteSubFolderLoading(false);
    }
  };

  const handleRenameSubFolder = async () => {
    if (!renameSubFolderName.trim()) {
      setRenameSubFolderError('Folder name cannot be empty');

      return;
    }
    setRenameSubFolderLoading(true);
    setRenameSubFolderError('');
    try {
      await uploadApi.groupSubFolder.rename(
        regulationContext.regulation.id,
        selectedSubFolder.id,
        renameSubFolderName.trim(),
        'master',
      );
      setShowRenameSubFolderModal(false);
      groupDocsQuery.refetch();
    } catch (e) {
      setRenameSubFolderError(apiErrorMessage(e, 'Failed to rename folder'));
    } finally {
      setRenameSubFolderLoading(false);
    }
  };

  const docs = useMemo(() => {
    const organizeDocs = () => {
      const jt =
        (groupDocsQuery.data && groupDocsQuery.data.group_docs_jt) || [];
      const newdoc = jt.filter((doc) => {
        if (doc.group_uploads) {
          return (
            doc.group_uploads.doctypes &&
            doc.group_uploads.doctypes[0] &&
            doc.group_uploads.doctypes[0].name === 'Fixed Details'
          );
        }

        return doc.doc_type
          ? doc.doc_type.name === 'Fixed Details'
          : doc.docType === 'Fixed Details';
      });

      return {
        bmp_docs: bmpDocsQuery.data,
        groupData: groupDocsQuery.data,
        fixedDetails: newdoc.length
          ? newdoc
          : [{ id: -1, mime_type: 'application/pdf' }],
      };
    };

    if (groupDocsQuery.data || bmpDocsQuery.data) {
      return organizeDocs();
    } else {
      return {};
    }
  }, [groupDocsQuery.data, bmpDocsQuery.data]);

  useEffect(() => {
    if (docs && docs.groupData) {
      updateGroup(docs.groupData);
    }
  }, [docs]);

  // An upload message belongs to the list it was raised on — drop it when the
  // user moves between the main list and a subfolder.
  useEffect(() => {
    setUploadError('');
  }, [selectedSubFolderId]);

  if (!groupDocsQuery.data || !bmpDocsQuery.data) {
    return <LoadingMessage what='Documents' />;
  }

  const searchProps = {
    query,
    setQuery(input) {
      setQuery(input);
    },
    placeholder: 'Search Regulations or Legend Details..',
  };

  function handleSubmit(formState) {
    if (!formState.name.trim()) {
      throw new Error('Regulation name cannot be empty');
    }

    return groupApi
      .update(regulationContext.regulation.id, formState)
      .then((group) => {
        updateGroup(regulationContext.regulation);
        setShowEdit(false);

        return Promise.resolve();
      });
  }

  const secondaryActions = [
    {
      label: 'Edit Group',
      icon: 'edit',
      onClick: () => setShowEdit(true),
    },
    {
      label: 'Delete Group',
      icon: 'trash',
      onClick: () => setShowDelete(true),
    },
  ];

  const handleDelete = () => {
    groupApi
      .destroy(regulationContext.regulation.id)
      .then(() => {
        setShowDelete(false);
        setDisableDelete(true);
        deleteGroup();
      })
      .catch((e) => {
        if (e.response && e.response.data && e.response.data.message) {
          setErrorMsg(e.response.data.message);
        } else {
          setErrorMsg('An error has occurred');
        }
      });
  };

  const showModal = () => {
    setShowDelete(true);
    setErrorMsg('');
  };

  const hideModal = () => {
    setShowDelete(false);
    setDisableDelete(true);
  };

  return regulationContext.regulation ? (
    <section className='project-detail group-detail'>
      <div className='group-header'>
        {regulationContext.regulation && regulationContext.regulation.name ? (
          <ResourceDetailTitle title={regulationContext.regulation.name} />
        ) : null}
        <div>
          {showEdit && (
            <GroupDetailsForm
              handleSubmit={handleSubmit}
              handleClose={() => setShowEdit(false)}
              title='Edit Group Details'
              group={regulationContext.regulation}
            />
          )}
          {showDelete && (
            <ResourceDetailDelete
              resource='Group'
              handleSubmit={handleDelete}
              confirmationMsg={
                <>
                  All documents and contacts associated with{' '}
                  <strong>{regulationContext.regulation.name}</strong> will be
                  deleted. Do you wish to proceed?
                </>
              }
              show={showDelete}
              errorMsg={errorMsg}
              hideModal={hideModal}
              showModal={showModal}
              disableDelete={disableDelete}
              toggleDisabled={() => setDisableDelete(!disableDelete)}
              icon={false}
            />
          )}
        </div>
      </div>
      {permCheck('update', 'Regulations') && (
        <ActionButtons secondary={secondaryActions} />
      )}
      <ResourceSearch local={false} {...searchProps} />

      <ResourceDetailTabs
        headers={panel}
        panels={[
          regulationContext.regulation
            ? () => (
                <TooltipContextProvider>
                  {/* ── SUBFOLDER VIEW ── */}
                  {selectedSubFolder && (
                    <div className='documents'>
                      <div
                        className='all-files-button'
                        onClick={() => setSelectedSubFolderId(null)}
                      >
                        <span className='all-files-button-icon'>
                          <FontAwesomeIcon icon={faArrowLeft} />
                        </span>
                        <span className='all-files-button-text'>All Files</span>
                      </div>
                      <div
                        className='document-group__header'
                        style={{ marginTop: '2rem', marginBottom: 4 }}
                      >
                        <h3 className='document-group__title'>
                          {selectedSubFolder.name}
                        </h3>
                        <div className='document-group__header-right'>
                          {permCheck('update', 'Regulations') &&
                            (selectedSubFolder.group_docs || []).length > 1 && (
                              <button onClick={handleOpenSubFolderOrganize}>
                                Organize
                              </button>
                            )}
                          {permCheck('update', 'Regulations') && (
                            <div
                              className='document-group__add-sub-folder'
                              onClick={() => {
                                setRenameSubFolderName(selectedSubFolder.name);
                                setRenameSubFolderError('');
                                setShowRenameSubFolderModal(true);
                              }}
                              title='Rename folder'
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </div>
                          )}
                          {permCheck('update', 'Regulations') && (
                            <div
                              className='document-group__add-sub-folder'
                              onClick={() => {
                                setDeleteSubFolderError('');
                                setShowDeleteSubFolderModal(true);
                              }}
                              title='Delete folder'
                            >
                              <FontAwesomeIcon icon={faTrashAlt} color='red' />
                            </div>
                          )}
                          {permCheck('update', 'Regulations') && (
                            <label
                              className='document-group__add-sub-folder'
                              title='Upload document'
                              style={{ cursor: 'pointer', marginBottom: 0 }}
                            >
                              <FontAwesomeIcon
                                icon={uploading ? faSpinner : faCloudUploadAlt}
                                spin={uploading}
                              />
                              <input
                                type='file'
                                accept='image/png,image/jpeg,application/pdf'
                                style={{ display: 'none' }}
                                disabled={uploading}
                                onChange={async (e) => {
                                  const file =
                                    e.target.files && e.target.files[0];

                                  if (!file) return;
                                  e.target.value = '';
                                  if (rejectUnsupportedFile(file)) return;
                                  setUploading(true);
                                  setUploadError('');
                                  try {
                                    const newFile =
                                      await uploadApi.create(file);

                                    await uploadApi.group.create(
                                      id,
                                      newFile.id,
                                      'Regulations',
                                      selectedSubFolder.id,
                                    );
                                    await groupDocsQuery.refetch();
                                  } catch (err) {
                                    setUploadError(
                                      'The document could not be uploaded. Please try again.',
                                    );
                                  } finally {
                                    setUploading(false);
                                  }
                                }}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                      {uploading && (
                        <div
                          className='document-group__uploading'
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 0',
                            color: '#48af98',
                            fontSize: 13,
                          }}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin />
                          <span>Uploading document…</span>
                        </div>
                      )}
                      {uploadError && (
                        <p
                          style={{
                            color: '#c0392b',
                            fontSize: 13,
                            padding: '4px 0',
                          }}
                        >
                          {uploadError}
                        </p>
                      )}
                      <FileRowList
                        key={selectedSubFolder.id}
                        groupName={selectedSubFolder.name}
                        documents={filteredSubFolderDocs.map((doc) => ({
                          ...doc,
                          GroupUploadModel:
                            doc.GroupUploadModel || doc.group_uploads,
                        }))}
                        canEdit={canEditRegulationDocs}
                        canDownload={true}
                        canDelete={permCheck('delete', 'Regulations')}
                        canReplace={false}
                        canUpload={permCheck('update', 'Regulations')}
                        canRename={permCheck('update', 'Regulations')}
                        canDrag={false}
                        hideTitle={true}
                        // The folder header already carries an upload button, so
                        // the list's drag-and-drop control would be a second way
                        // to do the same thing. Keep it only while the folder is
                        // empty, where it doubles as the empty-state prompt.
                        // Judged on the folder's real contents, not the filtered
                        // ones — a search that matches nothing should not bring
                        // the control back.
                        hideRowUploadControl={
                          (selectedSubFolder.group_docs || []).length > 0
                        }
                        allowedTypes={REGULATION_UPLOAD_TYPES}
                        handleDelete={async (doc) => {
                          await uploadApi.group.destroy(id, doc.id);
                          groupDocsQuery.refetch();
                        }}
                        handleEdit={(doc) => openRegulationDoc(doc, 'view')}
                        handleDownload={(doc) =>
                          openRegulationDoc(doc, 'download')
                        }
                        handleReplace={() => {}}
                        handleRename={(doc) => openRenameDoc(doc)}
                        onUpload={(newFile) =>
                          uploadToRegulations(newFile, selectedSubFolder.id)
                        }
                        label='Regulations'
                        permCheck={permCheck}
                        showAddSubFolderIcon={false}
                        canManageSubfolder={false}
                        selectedSubDocType={null}
                        setSelectedSubDocType={() => {}}
                        reloadSelectedSubDocTypeData={() => {}}
                        reloadDocs={() => {
                          setSelectedSubFolderId(null);
                          groupDocsQuery.refetch();
                        }}
                        onMove={() => {}}
                        onDrop={() => {}}
                      />
                    </div>
                  )}

                  {/* ── MAIN UNIFIED LIST VIEW ── */}
                  {!selectedSubFolder && (
                    <>
                      <div
                        className='document-group__header'
                        style={{ paddingBottom: 4 }}
                      >
                        <h3 className='document-group__title'>Regulations</h3>
                        <div className='document-group__header-right'>
                          {permCheck('update', 'Regulations') &&
                            unifiedDocs.length > 1 && (
                              <button onClick={handleOpenOrganize}>
                                Organize
                              </button>
                            )}
                          {permCheck('update', 'Regulations') && (
                            <button
                              className='btn btn-sm btn-outline-primary'
                              style={{ fontSize: 12, marginLeft: 8 }}
                              onClick={() => {
                                setNewSubFolderName('');
                                setSubFolderError('');
                                setShowAddSubFolderModal(true);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faPlus}
                                style={{ marginRight: 4 }}
                              />
                              Add Sub Folder
                            </button>
                          )}
                        </div>
                      </div>
                      {uploading && (
                        <div
                          className='document-group__uploading'
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 0',
                            color: '#48af98',
                            fontSize: 13,
                          }}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin />
                          <span>Uploading document…</span>
                        </div>
                      )}
                      {uploadError && (
                        <p
                          style={{
                            color: '#c0392b',
                            fontSize: 13,
                            padding: '4px 0',
                          }}
                        >
                          {uploadError}
                        </p>
                      )}
                      <FileRowList
                        groupName='Regulations'
                        documents={filteredDocs}
                        canEdit={canEditRegulationDocs}
                        canDownload={true}
                        canDelete={permCheck('delete', 'Regulations')}
                        canReplace={false}
                        canUpload={permCheck('update', 'Regulations')}
                        canRename={permCheck('update', 'Regulations')}
                        canDrag={false}
                        hideTitle={true}
                        allowedTypes={REGULATION_UPLOAD_TYPES}
                        handleDelete={async (doc) => {
                          await uploadApi.group.destroy(id, doc.id);
                          groupDocsQuery.refetch();
                        }}
                        handleEdit={(doc) => openRegulationDoc(doc, 'view')}
                        handleDownload={(doc) =>
                          openRegulationDoc(doc, 'download')
                        }
                        handleReplace={() => {}}
                        handleRename={(doc) => openRenameDoc(doc)}
                        onUpload={(newFile) => uploadToRegulations(newFile)}
                        label='Regulations'
                        permCheck={permCheck}
                        showAddSubFolderIcon={false}
                        canManageSubfolder={false}
                        selectedSubDocType={null}
                        setSelectedSubDocType={(item) =>
                          item &&
                          item.sub_doc_type &&
                          setSelectedSubFolderId(item.id)
                        }
                        reloadSelectedSubDocTypeData={() => {}}
                        reloadDocs={() => groupDocsQuery.refetch()}
                        onMove={() => {}}
                        onDrop={() => {}}
                      />
                    </>
                  )}

                  {/* Add subfolder modal */}
                  {showAddSubFolderModal && (
                    <div
                      className='modal-overlay'
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.4)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <div
                        className='modal-box'
                        style={{
                          background: '#fff',
                          borderRadius: 8,
                          padding: 24,
                          minWidth: 320,
                        }}
                      >
                        <h3 style={{ marginBottom: 12 }}>Add Sub Folder</h3>
                        <input
                          className='form-control'
                          value={newSubFolderName}
                          onChange={(e) => setNewSubFolderName(e.target.value)}
                          placeholder='Folder name'
                          autoFocus
                        />
                        {subFolderError && (
                          <p
                            style={{ color: 'red', fontSize: 12, marginTop: 4 }}
                          >
                            {subFolderError}
                          </p>
                        )}
                        <div
                          style={{
                            marginTop: 16,
                            display: 'flex',
                            gap: 8,
                            justifyContent: 'flex-end',
                          }}
                        >
                          <button
                            className='btn btn-secondary'
                            onClick={() => setShowAddSubFolderModal(false)}
                          >
                            Cancel
                          </button>
                          <button
                            className='btn btn-primary'
                            disabled={subFolderLoading}
                            onClick={handleAddSubFolder}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Organize mixed list modal */}
                  <Modal
                    title='Organize Documents'
                    show={showOrganizeModal && tempOrderedFolders.length > 0}
                    handleClose={() => setShowOrganizeModal(false)}
                    handleSubmit={handleSaveOrganize}
                    submitBtnText='Save'
                    errorMsg={organizeError}
                  >
                    <DragDropContext onDragEnd={handleOrganizeDragEnd}>
                      <Droppable droppableId='regulationDocs'>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {tempOrderedFolders.map((item, index) => (
                              <Draggable
                                key={`${item.sub_doc_type ? 'folder' : 'doc'}-${item.id}`}
                                draggableId={`${item.sub_doc_type ? 'folder' : 'doc'}-${item.id}`}
                                index={index}
                              >
                                {(provide) => (
                                  <div
                                    ref={provide.innerRef}
                                    {...provide.draggableProps}
                                    {...provide.dragHandleProps}
                                    style={{
                                      ...provide.draggableProps.style,
                                      left: 'auto !important',
                                      top: 'auto !important',
                                      marginBottom: '5px',
                                    }}
                                  >
                                    <span className='document-group-reorder-modal__draggable-file'>
                                      <FontAwesomeIcon
                                        icon='grip-lines'
                                        size='sm'
                                        color='#0d2330'
                                      />
                                      {item.sub_doc_type && (
                                        <FontAwesomeIcon
                                          icon={faFolder}
                                          color='#48af98'
                                          style={{
                                            marginLeft: 6,
                                            marginRight: 4,
                                          }}
                                        />
                                      )}
                                      <span className='document-group-reorder-modal__draggable-name'>
                                        {item.sub_doc_type
                                          ? item.sub_doc_type.name
                                          : item.name}
                                      </span>
                                    </span>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </Modal>

                  {/* Organize subfolder docs modal */}
                  <Modal
                    title='Organize Documents'
                    show={
                      showSubFolderOrganizeModal && tempSubFolderDocs.length > 0
                    }
                    handleClose={() => setShowSubFolderOrganizeModal(false)}
                    handleSubmit={handleSaveSubFolderOrganize}
                    submitBtnText='Save'
                    errorMsg={organizeError}
                  >
                    <DragDropContext onDragEnd={handleSubFolderOrganizeDragEnd}>
                      <Droppable droppableId='regulationSubFolderDocs'>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {tempSubFolderDocs.map((doc, index) => (
                              <Draggable
                                key={`subfolderdoc-${doc.id}`}
                                draggableId={`subfolderdoc-${doc.id}`}
                                index={index}
                              >
                                {(provide) => (
                                  <div
                                    ref={provide.innerRef}
                                    {...provide.draggableProps}
                                    {...provide.dragHandleProps}
                                    style={{
                                      ...provide.draggableProps.style,
                                      left: 'auto !important',
                                      top: 'auto !important',
                                      marginBottom: '5px',
                                    }}
                                  >
                                    <span className='document-group-reorder-modal__draggable-file'>
                                      <FontAwesomeIcon
                                        icon='grip-lines'
                                        size='sm'
                                        color='#0d2330'
                                      />
                                      <span className='document-group-reorder-modal__draggable-name'>
                                        {doc.name}
                                      </span>
                                    </span>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </Modal>

                  {/* Delete subfolder confirmation modal */}
                  <Modal
                    title='Are you sure you want to delete this Sub Folder?'
                    subTitle='Warning! This action cannot be undone.'
                    show={showDeleteSubFolderModal}
                    submitBtnText='Delete'
                    handleSubmit={handleDeleteSubFolder}
                    disableSubmit={deleteSubFolderLoading}
                    handleClose={() => setShowDeleteSubFolderModal(false)}
                    cancelBtn={true}
                    errorMsg={deleteSubFolderError}
                  >
                    <p>
                      The documents in this Sub Folder will not be deleted — they
                      are moved out to the bottom of the main Regulations list,
                      where they will stay until someone reorders them.
                    </p>
                    <p>
                      Every project using this Regulation Group is affected: the
                      same documents move to the bottom of that project's
                      Regulations list, and any custom order saved for this folder
                      or its documents is discarded. Please be sure before you
                      continue.
                    </p>
                  </Modal>

                  {/* Rename subfolder modal */}
                  <Modal
                    title='Rename Sub Folder'
                    show={showRenameSubFolderModal}
                    submitBtnText='Save'
                    handleSubmit={handleRenameSubFolder}
                    disableSubmit={renameSubFolderLoading}
                    handleClose={() => {
                      setShowRenameSubFolderModal(false);
                      setRenameSubFolderError('');
                    }}
                    cancelBtn={true}
                    errorMsg={renameSubFolderError}
                  >
                    <input
                      className='form-control'
                      value={renameSubFolderName}
                      onChange={(e) => setRenameSubFolderName(e.target.value)}
                      autoFocus
                    />
                  </Modal>
                </TooltipContextProvider>
              )
            : () => 'No Documents',
          () => (
            <TooltipContextProvider>
              <GroupDocuments
                scope='bmp'
                documentGroups={docs.bmp_docs}
                document_group_id={regulationContext.regulation.id}
                onDocumentUpload={onDocumentUpload}
                onDocumentEdit={onDocumentEdit}
                onDocumentDelete={onDocumentDelete}
                onMove={(e) => onMove(e, 'bmp')}
                onDrop={(e) => onDrop(e, 'regulations')}
                canDrag={permCheck('update', 'Regulations')}
                label={'Regulations'}
                roleCanDelete={permCheck('update', 'Regulations')}
                replace={permCheck('update', 'Regulations')}
                searchValue={search}
                fixedDetails={docs.fixedDetails}
                canEdit={!editPermissionList.includes(props.role)}
              />
            </TooltipContextProvider>
          ),
        ].filter((_) => _)}
        showTab={true}
      />

      {/* Document-level modals live outside `panels` on purpose. Those panels
          are inline arrow functions, so each render is a new component type and
          React remounts the whole subtree — a remounted WebViewer re-initialises
          PDFTron and downloads the file a second time. */}
      <WebViewerModal
        show={showEditor}
        onClose={closeRegulationViewer}
        onSave={() => groupDocsQuery.refetch()}
        guid={viewerDoc.guid}
        id={viewerDoc.id}
        uploadId={viewerDoc.id}
        filename={viewerDoc.name}
        disableDownload={!permCheck('all', 'Download')}
        canEdit={canEditRegulationDocs}
      />

      <WebViewerModal
        show={showDownload}
        onClose={closeRegulationViewer}
        onSave={() => groupDocsQuery.refetch()}
        guid={viewerDoc.guid}
        id={viewerDoc.id}
        uploadId={viewerDoc.id}
        filename={viewerDoc.name}
        canDownload={true}
        canEdit={canEditRegulationDocs}
      />

      <FileRenameModal
        show={showRename}
        file={{ guid: renameDoc.guid, name: renameDoc.name }}
        onCancel={() => setShowRename(false)}
        onSubmit={handleRenameDoc}
      />
    </section>
  ) : (
    <LoadingMessage what='Group Documents' />
  );
};

GroupDetailUI.propTypes = {
  history: PropTypes.shape({}).isRequired,
  match: PropTypes.shape({}).isRequired,
  permCheck: PropTypes.func.isRequired,
};

const GroupDetail = (props) => {
  const permCheck = useContext(RolesContext).userHasPermission;
  const appStore = useContext(AppContext);
  const user = appStore.get('user') || {};

  return (
    <GroupDetailUI {...props} permCheck={permCheck} role={user.roleName} />
  );
};

export default GroupDetail;
